/**
 * Game-scoped PeerBasket rendezvous.
 *
 * Challenge join links embed the lobby peerId, but the host board opens in a new
 * tab and must mint a new PeerJS session (IDs cannot be shared across tabs).
 * Joiner therefore dials a dead/wrong peer unless the live host peerId is
 * rediscovered under the game basket.
 */

import { parsePeerId } from '../security/input.js';
import { lobbyDebug } from './lobbyDebug.js';

const DEFAULT_PEERBASKET_BASE = 'https://peerbasket.bittu.dev/basket';
export const GAME_RENDEZVOUS_MIN_INTERVAL_MS = 3_000;
export const GAME_RENDEZVOUS_BACKOFF_MS = 15_000;

const gate = {
  nextAllowedAt: 0,
  inflight: null
};

function configuredBase() {
  const fromEnv = (import.meta.env?.VITE_LOBBY_RENDEZVOUS_URL || '').trim();
  if (fromEnv === 'off' || fromEnv === 'disabled') {
    return '';
  }
  if ((import.meta.env?.VITE_E2E_RELAY_URL || '').trim()) {
    return '';
  }
  if (import.meta.env?.MODE === 'test' || import.meta.env?.VITEST) {
    return '';
  }
  if (fromEnv === 'on' || fromEnv === 'default' || fromEnv === 'peerbasket') {
    return DEFAULT_PEERBASKET_BASE;
  }
  if (fromEnv) {
    return fromEnv.replace(/\/$/, '');
  }
  return DEFAULT_PEERBASKET_BASE;
}

export function gameRendezvousBasketId(gameId) {
  return `dechess.org:game:${String(gameId || '').trim()}`;
}

export function resetGameRendezvousState() {
  gate.nextAllowedAt = 0;
  gate.inflight = null;
}

/**
 * Post this peer into the game basket and return other live peer ids.
 */
export async function fetchGameRendezvousPeers(selfPeerId, gameId, {
  fetchImpl = globalThis.fetch,
  baseUrl = configuredBase(),
  now = Date.now(),
  force = false
} = {}) {
  const selfId = parsePeerId(selfPeerId);
  const id = String(gameId || '').trim();
  if (!selfId || !id || !baseUrl || typeof fetchImpl !== 'function') {
    return { peers: [] };
  }
  if (!force && now < gate.nextAllowedAt) {
    return { peers: [] };
  }
  if (gate.inflight) {
    return gate.inflight;
  }

  gate.inflight = (async () => {
    try {
      const basket = encodeURIComponent(gameRendezvousBasketId(id));
      const response = await fetchImpl(`${baseUrl}/${basket}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ peer_id: selfId }),
        mode: 'cors',
        signal: typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
          ? AbortSignal.timeout(8000)
          : undefined
      });
      if (!response.ok) {
        const error = new Error(`Game rendezvous failed (${response.status})`);
        error.status = response.status;
        throw error;
      }
      const body = await response.json();
      const raw = Array.isArray(body?.peers) ? body.peers : (Array.isArray(body) ? body : []);
      const peers = raw
        .map((entry) => parsePeerId(
          typeof entry === 'string' ? entry : (entry?.peer_id || entry?.peerId || '')
        ))
        .filter((peerId) => peerId && peerId !== selfId)
        .filter((peerId, index, list) => list.indexOf(peerId) === index);
      gate.nextAllowedAt = now + GAME_RENDEZVOUS_MIN_INTERVAL_MS;
      lobbyDebug('game-rendezvous-ok', {
        gameId: id,
        selfId,
        peers
      });
      return { peers };
    } catch (error) {
      const status = Number(error?.status) || 0;
      gate.nextAllowedAt = now + (
        status === 429 || status === 0
          ? GAME_RENDEZVOUS_BACKOFF_MS
          : GAME_RENDEZVOUS_MIN_INTERVAL_MS
      );
      lobbyDebug('game-rendezvous-fail', {
        gameId: id,
        selfId,
        status,
        message: error?.message || String(error)
      });
      return { peers: [] };
    } finally {
      gate.inflight = null;
    }
  })();

  return gate.inflight;
}
